import {Service} from "crqlar-react-common/dist/models/service/service.model"
import {Resource} from "crqlar-react-common/dist/models/resource/resource.model"
import {
    ResourceAvailabilityDTO
} from "crqlar-react-common/dist/models/resource/resourceAvailability.model"

interface AutoAssignParams {
    // The service the user has just picked - holds the requirements per resource category
    service?: Service
    // The pool the resources autocomplete offers (root resources, or tables for restaurants)
    resources?: Resource[]
    // Resources that are already on the reservation (manual picks must never be dropped here)
    selectedResourceIds?: string[]
    // Availability for the current time frame - used only as a preference, never as a hard filter,
    // because the availability query refetches whenever the service changes the end date
    resourcesWithAvailability?: ResourceAvailabilityDTO[]
}

export interface AutoAssignResult {
    // The full resourceIds list to put on the reservation (previous selection + auto-selected)
    resourceIds: string[]
    // Only the ids that this run added - kept by the caller so they can be released
    // when the user switches to another service
    addedResourceIds: string[]
}

/*
    Services define how many resources of a given category they need
    (`assignedResourcesRequirementPerCategory`, e.g. Swedish massage = 1 therapist + 1 facility).

    When a service is picked we fill those requirements automatically so the user
    doesn't have to hunt for the resources himself. Priority per category:

        1. resources linked to the service (service.resourceIds)
        2. resources that are not linked to the service - fallback when there aren't enough linked ones
        3. inside both groups: available resources first, then by name

    Resources already on the reservation count towards the requirement, so we only ever
    top up what is missing. When a category cannot be filled completely we still assign
    as much as possible - the goal is to save clicks, not to guarantee a complete assignment.
*/
export const autoAssignResourcesForService = ({
                                                  service,
                                                  resources,
                                                  selectedResourceIds,
                                                  resourcesWithAvailability
                                              }: AutoAssignParams): AutoAssignResult => {

    const resourceIds = [...(selectedResourceIds || [])]
    const addedResourceIds: string[] = []

    const requirements = service?.assignedResourcesRequirementPerCategory

    if (!service || !requirements || !resources?.length) {
        return {resourceIds, addedResourceIds}
    }

    const isLinked = (resource: Resource) =>
        service.resourceIds?.includes(resource.id as string) === true

    const isAvailable = (resource: Resource) =>
        resourcesWithAvailability?.find(r => r.id === resource.id)?.available === true

    Object.entries(requirements).forEach(([categoryId, requiredCount]) => {
        const required = requiredCount || 0
        if (!categoryId || required <= 0) return

        const resourcesOfCategory = resources.filter(r =>
            r.id && r.categoryIds?.includes(categoryId)
        )

        // Whatever is already selected for this category counts towards the requirement
        const alreadyAssigned = resourcesOfCategory.filter(r =>
            resourceIds.includes(r.id as string)
        ).length

        const missing = required - alreadyAssigned
        if (missing <= 0) return

        const candidates = resourcesOfCategory
            .filter(r => !resourceIds.includes(r.id as string))
            .sort((a, b) => {
                // 1. Linked resources win over not linked ones
                const linkedDiff = Number(isLinked(b)) - Number(isLinked(a))
                if (linkedDiff !== 0) return linkedDiff

                // 2. Then available ones - when availability is still loading nothing is
                //    treated as available, which simply leaves the order to the name
                const availableDiff = Number(isAvailable(b)) - Number(isAvailable(a))
                if (availableDiff !== 0) return availableDiff

                // 3. Keeps the outcome deterministic
                return (a.name || '').localeCompare(b.name || '')
            })
            .slice(0, missing)

        candidates.forEach(candidate => {
            resourceIds.push(candidate.id as string)
            addedResourceIds.push(candidate.id as string)
        })
    })

    return {resourceIds, addedResourceIds}
}

/*
    Drops the resources a previous service auto-assigned, so switching the service
    re-runs the assignment from scratch instead of piling up resources of the old service.
    Everything the user picked himself stays untouched.
*/
export const releaseAutoAssignedResources = (
    resourceIds: string[] | undefined,
    autoAssignedResourceIds: string[]
) => (resourceIds || []).filter(id => !autoAssignedResourceIds.includes(id))
